import React from "react";

/*
	La tira de meses de un acuerdo comercial (Etapa 3 de cobros): un cuadrito por mensualidad con su precio.
	Sirve para la vista previa del paso Promoción (descuento / lista / gratis / regalo) y para el estado de
	cuenta (pagado / pendiente / regalo / gratis / perdido). A2' (Esteban, 2026-10-08): un «regalo» es un mes que queda en $0
	SOLO al registrarse el pago adelantado completo; mientras tanto se cobra a precio normal (se enseña con su precio y
	borde punteado en oro). Colores de WAPP: naranja para lo que el acuerdo regala o rebaja, oro para lo que pide
	atención (regalo pendiente, perdido); nada de verde (solo Matriculado).
*/
export interface MesTira { n: number; title?: string; state: string; price?: number | null; list?: number | null; subTotal?: number | null; note?: string | null; }

const ETIQUETA: Record<string, string> = {
	pagado: "Pagado", pendiente: "Pendiente", regalo: "Regalo: queda en $0 al pagar el adelanto (mientras tanto se cobra normal)",
	regalo_parcial: "Regalo con tope (2x1): baja a este precio al pagar el adelanto (mientras tanto se cobra normal)", condicionado: "Regalo pendiente de pago adelantado", liberado: "Gratis", gratis: "Gratis (en $0 por el acuerdo)",
	perdido: "Perdió el descuento (pagó tarde)", no_cursada: "No cursada", descuento: "Con descuento", lista: "Precio de lista"
};

const estilo = (state: string): React.CSSProperties => {
	switch (state) {
		case "pagado": return { background: "var(--wa-papel)", borderColor: "var(--wa-filo)", color: "var(--wa-apagado)" };
		case "regalo": case "regalo_parcial": case "condicionado": return { background: "var(--wa-oro-suave)", borderColor: "var(--wa-oro)", borderStyle: "dashed", color: "var(--wa-tinta)" };
		case "perdido": return { background: "var(--wa-oro-suave)", borderColor: "var(--wa-oro)", color: "var(--wa-oro)" };
		case "gratis": case "liberado": return { background: "var(--wa-acento-suave)", borderColor: "var(--wa-acento)", color: "var(--wa-acento-texto)" };
		case "descuento": return { background: "var(--wa-acento-suave)", borderColor: "var(--wa-filo)", color: "var(--wa-tinta)" };
		case "no_cursada": return { background: "transparent", borderColor: "var(--wa-filo)", color: "var(--wa-tenue)", textDecoration: "line-through" };
		default: return { background: "var(--wa-superficie)", borderColor: "var(--wa-filo)", color: "var(--wa-tinta)" };
	}
};

const num = (n: number) => (Math.round(n * 100) / 100).toLocaleString("es-MX", { maximumFractionDigits: 2 });

/* Solo propiedades largas (borderWidth/borderStyle/borderColor): mezclar «border» con «borderStyle» hacía que React avisara
   al re-pintar y a veces dejara el borde punteado del regalo. */
const TiraDeMeses: React.FC<{ meses: MesTira[]; sym?: string; compacta?: boolean }> = ({ meses, sym = "", compacta }) => (
	<div style={{ display: "flex", flexWrap: "wrap", gap: 4, margin: "4px 0" }} aria-label="Tira de meses">
		{meses.map((m, i) => {
			const precio = m.price ?? m.subTotal ?? null;
			const regalo = m.state === "gratis" || m.state === "liberado";   // el regalo pendiente de adelanto enseña su precio normal
			return (
				<span key={"m" + i} title={`${m.title || (m.n === 0 ? "Inscripción" : "Mes " + m.n)} · ${ETIQUETA[m.state] || m.state}${precio !== null ? " · " + sym + num(precio) : ""}${m.list && precio !== null && precio < m.list ? " (lista " + sym + num(m.list) + ")" : ""}${m.note ? " · " + m.note : ""}`}
					style={{ borderWidth: 1, borderStyle: "solid", borderRadius: 6, padding: compacta ? "1px 5px" : "2px 7px", fontSize: compacta ? 11 : 12, lineHeight: 1.3, minWidth: compacta ? 34 : 46, textAlign: "center", fontVariantNumeric: "tabular-nums", ...estilo(m.state) }}>
					<b style={{ display: "block", fontSize: 10, letterSpacing: ".04em" }}>{m.n === 0 ? "INSC." : "M" + m.n}{m.state === "pagado" ? " ✓" : ""}</b>
					{regalo ? "Gratis" : precio !== null ? num(precio) : "—"}
				</span>
			);
		})}
	</div>
);

export const leyendaTira = "Pagado ✓ · Gratis en naranja · Regalo en oro punteado: se cobra normal hasta que se registre el pago adelantado completo (entonces queda en $0, o en el precio que marca si es un 2x1 con tope) · Sin color: precio normal · Oro: perdió el descuento por pagar tarde";

export default TiraDeMeses;
